// src/components/home/BannerSlider.jsx

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';

const BannerSlider = ({ interval = 6000 }) => {
    const [banners, setBanners] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const nextSlide = useCallback(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, [banners.length]);

    const prevSlide = useCallback(() => {
        setCurrentImageIndex((prevIndex) =>
            (prevIndex - 1 + banners.length) % banners.length
        );
    }, [banners.length]);

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const homeContent = await apiClient.getHomeContent();

                if (homeContent && homeContent.banner) {
                    const fetchedBanners = homeContent.banner.map(banner => ({
                        id: banner.orders,
                        src: banner.img,
                        alt: `Gambar Banner ${banner.orders}`,
                    }));
                    setBanners(fetchedBanners);
                } else {
                    throw new Error('Data format tidak sesuai atau banner tidak ditemukan.');
                }
            } catch (e) {
                console.error("Gagal mengambil data banner:", e);
                setError('Gagal memuat banner. Silakan coba lagi nanti.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchBanners();
    }, []);

    useEffect(() => {
        if (banners.length > 1) {
            const timer = setInterval(() => {
                nextSlide();
            }, interval);
            return () => clearInterval(timer);
        }
    }, [banners.length, interval, nextSlide]);

    if (isLoading) {
        return (
            <div className="relative w-full h-[300px] md:h-[500px] lg:h-[600px] rounded-xl overflow-hidden shadow-lg flex items-center justify-center bg-gray-200">
                <p className="text-gray-600">Memuat banner...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="relative w-full h-[300px] md:h-[500px] lg:h-[600px] rounded-xl overflow-hidden shadow-lg flex items-center justify-center bg-red-100">
                <p className="text-red-800">{error}</p>
            </div>
        );
    }

    if (banners.length === 0) {
        return (
            <div className="relative w-full h-[300px] md:h-[500px] lg:h-[600px] rounded-xl overflow-hidden shadow-lg flex items-center justify-center bg-gray-200">
                <p className="text-gray-600">Tidak ada banner yang tersedia.</p>
            </div>
        );
    }

    return (
        <div className="relative w-full h-[600px] md:h-[700px] rounded-xl overflow-hidden">
            {banners.map((banner, index) => (
                <img
                    key={banner.id}
                    src={banner.src}
                    alt={banner.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                />
            ))}

            <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-50 rounded-full text-gray-800 hover:bg-opacity-75 transition-colors duration-200 focus:outline-none"
                aria-label="Previous slide"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-50 rounded-full text-gray-800 hover:bg-opacity-75 transition-colors duration-200 focus:outline-none"
                aria-label="Next slide"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                {banners.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`h-2 w-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-white w-6' : 'bg-gray-400'}`}
                        aria-label={`Go to slide ${index + 1}`}
                    ></button>
                ))}
            </div>
        </div>
    );
};

export default BannerSlider;